/**
 * The onboarding stage's tunnel (`spec-onboarding-persistent-scene-stage`, R2): where a step offers
 * the stage its **layer** (what to draw) and its **slot** (where on screen), and where the stage
 * reads them. In the page's own chunk: it imports nothing but React and types, so the page never
 * pulls three.js or Mapbox in through it. The stage itself (`PlanStage`) is in the renderer's chunk.
 *
 * - **One external store per page**, never React state of the page: a layer changes on every pointer
 *   move of a drag (its origin), and the page must not re-render for it. The stage subscribes.
 * - **One layer at a time.** A step registers on mount and withdraws on unmount, both in layout
 *   effects, so a draft swap's old step withdraws before the new one offers in the same commit. A
 *   second offer while one is held replaces it, with one development warning (spec, Inputs). The
 *   held layer withdrawing hands the stage back to the latest offer still registered (a step
 *   returned to while it was fading out, `spec-onboarding-persistent-scene-wash`, PR review).
 * - **Slot moves are synchronous.** The stage registers a mover, called inside `setSlot` and
 *   `withdraw`, so the map is moved out of a slot while the slot is still in the document (React runs
 *   a step's layout-effect cleanup and ref detach before it removes the step's DOM).
 */
import { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { PlanDrawState } from './planDrawState';
import type { PlanPose, PlanViewState } from './planMapLock';

/** R3: what a step's scene is. One entry per step, in `onboardingSteps.ts`'s `STEP_SCENES`. */
export interface StageScene {
  /** `plan-view` holds the top-down lock (a step's own control may still release it). */
  camera: 'plan-view' | 'free';
  /**
   * `none`: no map shown. `clear`: the map as drawn. `washed`: the map under the page's frosted
   * `Scrim`, drawn by the page while the washed plan has drawn (`spec-onboarding-persistent-scene-wash`,
   * R3); the stage itself draws `washed` as `clear`.
   */
  underlay: 'none' | 'clear' | 'washed';
  /** Whether Mapbox's own gestures are on while locked. */
  interactive: boolean;
  /**
   * Where the map is shown (`spec-onboarding-persistent-scene-full-bleed`, R2): `pane`, the step's
   * own slot; `page`, the page's full-window backdrop under the glass header and step list (the
   * step then needs no slot). Absent reads as `pane`.
   */
  placement?: 'pane' | 'page';
}

/** A step that shows no map; also the entry while the wizard decides its landing step. */
export const NO_SCENE: StageScene = {
  camera: 'plan-view',
  underlay: 'none',
  interactive: false,
  placement: 'pane',
};

/**
 * The five non-plan steps' scene (`spec-onboarding-persistent-scene-wash`, R1.1): the plan from above
 * on the page's backdrop, washed and not interactive. One constant, so every washed step's entry is
 * the same and a move between two of them changes nothing the stage reads.
 */
export const WASHED_SCENE: StageScene = {
  camera: 'plan-view',
  underlay: 'washed',
  interactive: false,
  placement: 'page',
};

/** R2.2: what a step draws on the stage, and the map-level values it owns. */
export interface StageLayer {
  /** The 3D content's anchor. */
  origin: { latitude: number; longitude: number };
  /** Where the camera stands on each arrival, and where a new map starts. */
  startPose: { longitude: number; latitude: number; zoom: number; bearing: number };
  /** The layer's own lock request (a turn-and-tilt gesture releases it into the free camera). */
  planView: boolean;
  /**
   * Released, the lock's free camera, where heights follow the tilt
   * (`spec-onboarding-persistent-scene-lock-poses`, R1). Absent, `off`.
   */
  free?: boolean;
  /** Where the next release eases to, in one move (R2). Hold it stable: layers compare by identity. */
  leavePose?: PlanPose | null;
  /** Where the next lock eases to, flat (R3). Hold it stable, as `leavePose`. */
  enterPose?: PlanPose | null;
  /** Bumped by the step's Retry: the stage rebuilds its map (R4.3). */
  retryKey: number;
  /** The 3D content, wrapped in every provider it reads: the stage passes none of its own. */
  content: ReactNode;
  onMap?: (map: MapboxMap | null) => void;
  onDrawState?: (state: PlanDrawState) => void;
  onPlanViewState?: (state: PlanViewState) => void;
}

/** What the stage reads: the held registration, its layer and its slot, and the page's backdrop. */
export interface StageHeld {
  id: number | null;
  layer: StageLayer | null;
  slot: HTMLElement | null;
  /**
   * The page's full-window backdrop (`spec-onboarding-persistent-scene-full-bleed`, R1.2): the page's,
   * not a step's, so it outlives every registration. `null` before the page mounts it and after.
   */
  backdrop: HTMLElement | null;
}

export interface StageTunnelStore {
  get(): StageHeld;
  subscribe(listener: () => void): () => void;
  /**
   * The stage's slot mover, called synchronously with the slot now held (or `null`) whenever the
   * slot or the backdrop changes or a layer comes or goes.
   */
  setMover(mover: ((slot: HTMLElement | null) => void) | null): void;
  /**
   * The page hands over its backdrop once mounted, and `null` on unmount. Calls the mover at once,
   * as `setSlot` does, so the map moves while both elements are still in the document.
   */
  setBackdrop(backdrop: HTMLElement | null): void;
  offer(id: number): void;
  update(id: number, layer: StageLayer | null): void;
  setSlot(id: number, slot: HTMLElement | null): void;
  withdraw(id: number): void;
}

const EMPTY: StageHeld = { id: null, layer: null, slot: null, backdrop: null };

let warnedSecondLayer = false;

const sameLayer = (a: StageLayer | null, b: StageLayer | null) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (Object.keys(a) as (keyof StageLayer)[]).every(k => a[k] === b[k]);
};

export const createStageTunnelStore = (): StageTunnelStore => {
  let held = EMPTY;
  // Every registration's latest layer and slot: a step's slot ref attaches before its layout
  // effect registers it.
  const layers = new Map<number, StageLayer | null>();
  const slots = new Map<number, HTMLElement | null>();
  // The registrations still mounted, oldest offer first.
  const offered: number[] = [];
  const listeners = new Set<() => void>();
  let mover: ((slot: HTMLElement | null) => void) | null = null;

  const set = (next: StageHeld) => {
    // The mover places the map by the slot, the backdrop and by whether a layer is held (no layer,
    // no map shown).
    const move =
      next.slot !== held.slot || next.backdrop !== held.backdrop || !next.layer !== !held.layer;
    held = next;
    if (move) mover?.(next.slot);
    listeners.forEach(l => l());
  };

  return {
    get: () => held,
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setMover: next => {
      mover = next;
    },
    offer: id => {
      if (held.id !== null && held.id !== id && process.env.NODE_ENV !== 'production') {
        if (!warnedSecondLayer) {
          warnedSecondLayer = true;
          console.warn('[PlanStage] a second layer was offered while one was held');
        }
      }
      const at = offered.indexOf(id);
      if (at >= 0) offered.splice(at, 1);
      offered.push(id);
      set({ ...held, id, layer: layers.get(id) ?? null, slot: slots.get(id) ?? null });
    },
    update: (id, layer) => {
      layers.set(id, layer);
      if (held.id !== id || sameLayer(held.layer, layer)) return;
      set({ ...held, layer });
    },
    setSlot: (id, slot) => {
      // A detached slot is forgotten: a step's ref detaches after its withdraw.
      if (slot) slots.set(id, slot);
      else slots.delete(id);
      if (held.id !== id || held.slot === slot) return;
      set({ ...held, slot });
    },
    withdraw: id => {
      layers.delete(id);
      slots.delete(id);
      const at = offered.indexOf(id);
      if (at >= 0) offered.splice(at, 1);
      if (held.id !== id) return;
      const back = offered.length > 0 ? offered[offered.length - 1] : null;
      set(
        back === null
          ? { ...EMPTY, backdrop: held.backdrop }
          : {
              ...held,
              id: back,
              layer: layers.get(back) ?? null,
              slot: slots.get(back) ?? null,
            },
      );
    },
    setBackdrop: backdrop => {
      if (held.backdrop === backdrop) return;
      set({ ...held, backdrop });
    },
  };
};

const StageTunnelContext = createContext<StageTunnelStore | null>(null);

/**
 * Provides a tunnel to the steps below. The store is the page's, made once with
 * `createStageTunnelStore` and held outside every key, so a provider inside a keyed subtree (the
 * draft key) still hands every step the same store. Without one, it makes its own (tests).
 */
export const PlanStageTunnel = ({
  store: given,
  children,
}: {
  store?: StageTunnelStore;
  children?: ReactNode;
}) => {
  const [own] = useState(createStageTunnelStore);
  return <StageTunnelContext.Provider value={given ?? own}>{children}</StageTunnelContext.Provider>;
};

/** The stage's read of the tunnel; `null` outside one. */
export const useStageTunnel = () => useContext(StageTunnelContext);

let nextRegistration = 0;

/**
 * A step offers `layer` (`null` while it has none, such as before its anchor) and gets the ref for
 * its slot. Outside a tunnel it does nothing.
 */
export const useStageLayer = (layer: StageLayer | null) => {
  const store = useContext(StageTunnelContext);
  const idRef = useRef(0);
  if (idRef.current === 0) idRef.current = nextRegistration += 1;
  const id = idRef.current;
  useLayoutEffect(() => {
    if (!store) return undefined;
    store.offer(id);
    return () => store.withdraw(id);
  }, [store, id]);
  // Every commit: the store compares and tells the stage only of a change.
  useLayoutEffect(() => {
    store?.update(id, layer);
  });
  return useCallback((slot: HTMLElement | null) => store?.setSlot(id, slot), [store, id]);
};

/** Tests only: the once-per-page warning, re-armed. */
export const resetPlanStageWarningsForTests = () => {
  warnedSecondLayer = false;
};
